/**
 * Production-safe logger utility
 *
 * In development: Logs to console with full details
 * In production: Logs are stripped (no-op) to prevent exposing internal logic
 *
 * SECURITY: This prevents exposing internal URLs, API endpoints, and debug
 * information to end users via browser console logs.
 *
 * Usage:
 *   import { logger } from '@shared/utils/logger';
 *   logger.log('Debug info', { data });
 *   logger.error('Error details', error);
 *
 * @example
 * // Development mode (import.meta.env.DEV = true)
 * logger.log('Fetching campaigns...'); // ✓ Logs to console
 *
 * // Production mode (import.meta.env.DEV = false)
 * logger.log('Fetching campaigns...'); // ✗ No output (stripped)
 */

const isDevelopment = import.meta.env.DEV;

export const logger = {
  /**
   * Log general information (development only)
   *
   * Use for debugging, tracing execution flow, and non-critical information.
   *
   * @param args - Arguments to log
   *
   * NOTE: `any[]` is intentional here for variadic console.log compatibility.
   * Console methods accept any type and count, making this a legitimate use case.
   */
  log: (...args: any[]): void => {
    if (isDevelopment) {
      console.log(...args);
    }
  },

  /**
   * Log warnings (development only)
   *
   * Use for deprecated features, potential issues, and non-blocking problems.
   *
   * @param args - Arguments to log
   *
   * NOTE: `any[]` is intentional here for variadic console.warn compatibility.
   */
  warn: (...args: any[]): void => {
    if (isDevelopment) {
      console.warn(...args);
    }
  },

  /**
   * Log errors (always logged, but sanitized in production)
   *
   * In development: Full error details
   * In production: Generic sanitized message (actual errors go to server logs)
   *
   * SECURITY: Production logs do not expose stack traces, API endpoints, or
   * internal implementation details to prevent information disclosure attacks.
   *
   * @param args - Arguments to log
   *
   * NOTE: `any[]` is intentional here for variadic console.error compatibility.
   */
  error: (...args: any[]): void => {
    if (isDevelopment) {
      console.error(...args);
    } else {
      // In production, log sanitized error without sensitive details
      // Actual error details should be captured by error monitoring service
      console.error('An error occurred. Please contact support if the issue persists.');
    }
  },

  /**
   * Log informational messages (development only)
   *
   * Alias for `log` - use for general informational output.
   *
   * @param args - Arguments to log
   *
   * NOTE: `any[]` is intentional here for variadic console.info compatibility.
   */
  info: (...args: any[]): void => {
    if (isDevelopment) {
      console.info(...args);
    }
  },

  /**
   * Log debugging information (development only)
   *
   * Use for verbose debugging, variable inspection, and detailed traces.
   *
   * @param args - Arguments to log
   *
   * NOTE: `any[]` is intentional here for variadic console.debug compatibility.
   */
  debug: (...args: any[]): void => {
    if (isDevelopment) {
      console.debug(...args);
    }
  },

  /**
   * Group logs together (development only)
   *
   * Use to organize related log messages into collapsible groups.
   *
   * @param label - Group label
   */
  group: (label: string): void => {
    if (isDevelopment) {
      console.group(label);
    }
  },

  /**
   * End log group (development only)
   */
  groupEnd: (): void => {
    if (isDevelopment) {
      console.groupEnd();
    }
  },

  /**
   * Log a table (development only)
   *
   * Use for displaying structured data in tabular format.
   *
   * @param data - Data to display as table
   * @param columns - Optional columns to display
   *
   * NOTE: `any` is intentional here - console.table accepts any tabular data.
   */
  table: (data: any, columns?: string[]): void => {
    if (isDevelopment) {
      console.table(data, columns);
    }
  },

  /**
   * Start a timer (development only)
   *
   * Use to measure execution time of operations.
   *
   * @param label - Timer label
   */
  time: (label: string): void => {
    if (isDevelopment) {
      console.time(label);
    }
  },

  /**
   * End a timer (development only)
   *
   * @param label - Timer label (must match time() label)
   */
  timeEnd: (label: string): void => {
    if (isDevelopment) {
      console.timeEnd(label);
    }
  },
};

/**
 * Create a scoped logger with a prefix
 *
 * Useful for creating module-specific loggers that automatically
 * prefix all messages with the module name.
 *
 * @param prefix - Prefix for all log messages
 * @returns Scoped logger object
 *
 * @example
 * const campaignLogger = createScopedLogger('[Campaign Service]');
 * campaignLogger.log('Fetching campaigns...'); // Outputs: [Campaign Service] Fetching campaigns...
 */
export const createScopedLogger = (prefix: string) => {
  return {
    // NOTE: `any[]` is intentional for variadic console method compatibility
    log: (...args: any[]): void => logger.log(prefix, ...args),
    info: (...args: any[]): void => logger.info(prefix, ...args),
    warn: (...args: any[]): void => logger.warn(prefix, ...args),
    error: (...args: any[]): void => logger.error(prefix, ...args),
    debug: (...args: any[]): void => logger.debug(prefix, ...args),
    group: (label: string): void => logger.group(`${prefix} ${label}`),
    groupEnd: (): void => logger.groupEnd(),
    // NOTE: `any` is intentional - console.table accepts any tabular data
    table: (data: any, columns?: string[]): void => logger.table(data, columns),
    time: (label: string): void => logger.time(`${prefix} ${label}`),
    timeEnd: (label: string): void => logger.timeEnd(`${prefix} ${label}`),
  };
};

export default logger;
